import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionAccess } from '../../hooks/useSubscriptionAccess';
import { Spinner } from '../../design-system';

interface SubscriptionRequiredRouteProps {
  children?: React.ReactNode;
  allowWithoutSubscription?: boolean;
}

const SubscriptionRequiredRoute = ({
  children,
  allowWithoutSubscription = false
}: SubscriptionRequiredRouteProps) => {
  const { isAuthenticated, isLoading: authLoading, isAdmin } = useAuth();
  const { hasAccess, loading: accessLoading, error: accessError } = useSubscriptionAccess();
  const location = useLocation();

  // Check if this is an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Show loading state while checking auth and access
  if (authLoading || accessLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Spinner size={48} className="mx-auto mb-4" />
          <p className="text-ui-text-secondary">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Admin users on admin routes don't need subscription
  if (isAdminRoute && isAdmin) {
    return children ? <>{children}</> : <Outlet />;
  }

  // If access check failed due to auth (e.g., 401), route to login instead of subscription page
  if (!hasAccess && accessError === 'auth') {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Authenticated but no access - redirect to subscription page
  if (!hasAccess && !allowWithoutSubscription) {
    return <Navigate to="/subscription-required" state={{ from: location }} replace />;
  }

  // All checks passed - render content
  return children ? <>{children}</> : <Outlet />;
};

export default SubscriptionRequiredRoute; 